import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Icon from './icons';
import { makeFmt } from '../../../schedule/format';
import { colorSlot, nowIn, ymdParts } from '../../../schedule/model';

const bold = { b: <strong /> };

/**
 * The "this will pause the schedule" dialog (schedule v4). ONE component for every place a person changes what a screen shows
 * while a schedule window is playing: the page's «عرض حملة أخرى الآن» (`pick`: choose a campaign here), and the Screens page /
 * Sources picker / other assign dialogs (the campaign is already chosen, so only the warning + confirm).
 *
 *   screens  preview rows [{ device_id, name, schedule_name, until_local, until_at }] of the screens that are inside a window
 *   total    how many screens the company has (to say "all screens")
 *   shows    [{ id, name, hint?, slot? }]   (pick mode only)
 *   exclude  the campaign playing now (not offered again)
 *   the picked campaign's name is added to the warning ("…and X will show instead")
 */
export default function HoldDialog({ pick = false, screens, total, shows = [], exclude = null, tz, loading = false, failed = false, busy = false, today: todayProp, onConfirm, onClose }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'ar').startsWith('en') ? 'en' : 'ar';
  const f = useMemo(() => makeFmt(t, lang), [t, lang]);
  const [picked, setPicked] = React.useState(null);
  const today = todayProp || nowIn(tz).date;
  const year = ymdParts(today).y;
  const who = (n) => (n >= total ? t('sch6.who_all') : f.nScreens(n));

  // one line per (schedule, until)
  const groups = [];
  (screens || []).forEach((s) => {
    const key = `${s.schedule_name}|${s.until_at}`;
    let g = groups.find((x) => x.key === key);
    if (!g) { g = { key, name: s.schedule_name || '', until: f.untilTxt(s.until_local, s.until_at, tz, today, year), n: 0 }; groups.push(g); }
    g.n += 1;
  });
  const pickedName = shows.find((s) => s.id === picked)?.name ?? null;
  const instead = pickedName ? t('sch6.m_instead', { show: pickedName }) : '';
  const totalN = (screens || []).length;
  const canGo = !loading && !failed && !busy && totalN > 0 && (!pick || !!picked);
  const current = groups[0]?.name;

  return (
    <div className="sch6-scrim" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sch6-sheet sch6-sm" role="dialog" aria-modal="true" aria-labelledby="sch6-mT">
        <div className="sch6-sh-top">
          <h3 id="sch6-mT">{t('sch6.m_title')}</h3>
          <button type="button" className="sch6-icon-btn" onClick={onClose} aria-label={t('sch6.close')}><Icon name="x" /></button>
        </div>
        <div className="sch6-sh-body">
          {pick && (
            <>
              <p className="sch6-q">{t('sch6.m_q', { who: who(totalN || total) })}</p>
              <div className="sch6-pick">
                {shows.filter((s) => s.id !== exclude).map((s) => (
                  <button type="button" key={s.id} className="sch6-opt" aria-pressed={picked === s.id} onClick={() => setPicked(s.id)}>
                    <span className="sch6-thumb" style={{ background: `linear-gradient(135deg,var(--s${s.slot ?? colorSlot(s.id)}), color-mix(in srgb,var(--s${s.slot ?? colorSlot(s.id)}) 45%, var(--card)))` }} />
                    <span style={{ minWidth: 0 }}><b>{s.name}</b>{s.hint ? <small>{s.hint}</small> : null}</span>
                  </button>
                ))}
              </div>
            </>
          )}
          {loading && <p className="sch6-note" role="status"><Icon name="clock" /><span>{t('sch6.m_loading')}</span></p>}
          {failed && <p className="sch6-note sch6-warnnote" role="alert"><Icon name="alert" /><span>{t('sch6.m_failed')}</span></p>}
          {!loading && !failed && groups.length === 1 && (
            <div className="sch6-held" style={{ marginTop: 0 }}><Icon name="alert" />
              <span><Trans i18nKey="sch6.m_warn" values={{ name: current, who: who(totalN), until: groups[0].until, instead }} components={bold} /></span>
            </div>
          )}
          {!loading && !failed && groups.length > 1 && (
            <div className="sch6-held" style={{ marginTop: 0 }}><Icon name="alert" />
              <span>{t('sch6.m_warn_multi')}{groups.map((g) => <span key={g.key} style={{ display: 'block' }}>{t('sch6.m_warn_line', { name: g.name, who: who(g.n), until: g.until })}</span>)}{instead ? ` ${instead.trim()}` : ''}</span>
            </div>
          )}
          {pick && <p className="sch6-note"><Icon name="screen" /><span>{t('sch6.m_note')}</span></p>}
        </div>
        <div className="sch6-sh-foot">
          <button type="button" className="sch6-btn sch6-ghost" onClick={onClose}>{t('sch6.cancel')}</button>
          <button type="button" className="sch6-btn sch6-pri" disabled={!canGo} onClick={() => onConfirm(picked)} autoFocus={!pick}>{t(pick ? 'sch6.m_confirm' : 'sch6.m_continue')}</button>
        </div>
      </div>
    </div>
  );
}
